<template>
  <div class="flex flex-col">
    <div>
      <div v-if="filtered_templates.length" class="flex flex-wrap items-start min-w-full">
        <div
          v-for="template in filtered_templates?.slice(0, show_all_templates ? 999 : 18)"
          :key="template.id"
          tabindex="1"
          class="relative flex-auto w-[300px] max-w-[300px] min-h-[136px] rounded-xl bg-white px-3 py-3 shadow dark:bg-dark-screen-background border dark:border-dark-divider-border mx-1 mb-3 flex items-center space-x-3">
          <div class="flex-1 min-w-0 truncate overflow-ellipsis">
            <div class="flex flex-col w-full">
              <div class="focus:outline-none">
                <p class="w-full flex flex-col text-[17px] font-medium text-common-grey has-tooltip">
                  <span
                    class="truncate block font-normal text-light-black-text dark:text-dark-title-text overflow-hidden overflow-ellipsis whitespace-nowrap max-w-min">
                    {{ template.name }}
                  </span>
                  <span
                    class="tooltip rounded-md bg-light-screen-background font-normal text-light-black-text dark:text-dark-title-text overflow-hidden overflow-ellipsis whitespace-nowrap max-w-min">
                    {{ template.name }}
                  </span>
                </p>
              </div>
              <div class="flex items-center">
                <button
                  :disabled="sourceObjectType == 'milestone'"
                  type="button"
                  class="m-0 px-2 w-fit"
                  :class="[sourceObjectType == 'milestone' ? 'primary-btn hover:cursor-default' : 'primary-outline-btn']"
                  @click="useTemplate(template)">
                  <Plus class="h-5 w-5" aria-hidden="true" />
                  {{ $t("Use template") }}
                </button>
                <div class="has-tooltip">
                  <Building2 v-if="template?.content?.companies?.length" :color="color" class="w-4 h-4 ml-2" />
                  <div class="tooltip flex p-2 text-sm font-medium border rounded-md border-dark-brand-blue bg-light-screen-background">
                    {{ $t("Company") }}
                  </div>
                </div>
                <div class="has-tooltip">
                  <Numbers2Icon v-if="template?.content?.tags?.length" :color="color" class="w-4 h-4 ml-2" />
                  <div class="tooltip flex p-2 text-sm font-medium border rounded-md border-dark-brand-blue bg-light-screen-background">
                    {{ $t("Tags") }}
                  </div>
                </div>
                <div class="has-tooltip">
                  <Share2 v-if="template?.content?.assigned_to?.length" :color="color" class="w-4 h-4 ml-2" />
                  <div class="tooltip flex p-2 text-sm font-medium border rounded-md border-dark-brand-blue bg-light-screen-background">
                    {{ $t("Users") }}
                  </div>
                </div>
                <div class="has-tooltip">
                  <AlignLeft v-if="template?.content?.description" :color="color" class="w-4 h-4 ml-2" />
                  <div class="tooltip flex p-2 text-sm font-medium border rounded-md border-dark-brand-blue bg-light-screen-background">
                    {{ $t("Description") }}
                  </div>
                </div>
                <div class="has-tooltip">
                  <CalendarDays
                    v-if="template?.content?.due_date || template?.content?.date || template?.content?.interaction_date"
                    :color="color"
                    class="w-4 h-4 ml-2" />
                  <div class="tooltip flex p-2 text-sm font-medium border rounded-md border-dark-brand-blue bg-light-screen-background">
                    {{ $t("Date") }}
                  </div>
                </div>
                <div class="has-tooltip">
                  <ClipboardCheck
                    v-if="template?.content?.tasks?.length || template?.content?.steps?.filter((s) => s.tasks.length)"
                    :color="color"
                    class="w-4 h-4 ml-2" />
                  <div class="tooltip flex p-2 text-sm font-medium border rounded-md border-dark-brand-blue bg-light-screen-background">
                    {{ $t("Tasks") }}
                  </div>
                </div>
                <div class="has-tooltip">
                  <Star
                    v-if="template?.content?.goals?.length || template?.content?.steps?.filter((s) => s.goals.length)"
                    :color="color"
                    class="w-4 h-4 ml-2" />
                  <div class="tooltip flex p-2 text-sm font-medium border rounded-md border-dark-brand-blue bg-light-screen-background">
                    {{ $t("Goals") }}
                  </div>
                </div>
                <div class="has-tooltip">
                  <Paperclip v-if="template?.content?.attachments?.length" :color="color" class="w-4 h-4 ml-2" />
                  <div class="tooltip flex p-2 text-sm font-medium border rounded-md border-dark-brand-blue bg-light-screen-background">
                    {{ $t("Attachments") }}
                  </div>
                </div>
              </div>
            </div>
            <hr class="w-full mt-2 border-light-divider-blue dark:border-dark-divider-border">
            <div class="flex w-full h-[30px] items-center cursor-pointer mt-2">
              <div class="w-1/3 flex items-center justify-center group" @click="editTemplate(template)">
                <div class="flex flex-col justify-center items-center">
                  <Pencil class="w-5 h-5 text-gray-400 group-hover:text-[#27C884]" aria-hidden="true" />
                  <span class="text-sm font-medium hidden group-hover:block">{{ $t("Edit") }}</span>
                </div>
              </div>
              <div class="w-1/3 flex items-center justify-center group" @click="duplicateTemplate(template)">
                <div class="flex flex-col justify-center items-center">
                  <Copy class="w-5 h-5 text-gray-400 group-hover:text-[#2DA7D7]" aria-hidden="true" />
                  <span class="text-sm font-medium hidden group-hover:block">{{ $t("Duplicate") }}</span>
                </div>
              </div>
              <div
                class="w-1/3 flex items-center justify-center group"
                @click="$emitter.emit(ALL_EMITTERS.DELETE_TEMPLATE, { template: template })">
                <div class="flex flex-col justify-center items-center">
                  <Trash2 class="w-5 h-5 text-gray-400 group-hover:text-red-500" aria-hidden="true" />
                  <span class="text-sm font-medium hidden group-hover:block">{{ $t("Delete") }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="bg-white dark:bg-dark-box-background flex justify-center min-h-[500px] overflow-hidden rounded-lg p-5">
        <div class="flex flex-col items-center justify-center">
          <Info class="w-20 h-20 text-blue" aria-hidden="true" />
          <h3 class="mt-2 text-xl font-normal text-black dark:text-white">
            {{ $t("No Templates found") }}.
          </h3>
        </div>
      </div>
      <div v-if="!show_all_templates && cloned_templates.length > 18 && showLoadMore">
        <div class="flex w-full justify-center mt-4">
          <button
            class="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
            @click="show_all_templates = true">
            {{ $t("Load More") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dataService from "@/services/data.service"
import Numbers2Icon from "@/components/shared/icons/Numbers2Icon.vue"
import {
  Share2,
  AlignLeft,
  Paperclip,
  ClipboardCheck,
  Building2,
  Plus,
  Trash2,
  Pencil,
  Copy,
  Info,
  Star,
  CalendarDays
} from "lucide-vue-next"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"

export default {
  components: {
    Building2,
    Numbers2Icon,
    Share2,
    AlignLeft,
    CalendarDays,
    ClipboardCheck,
    Star,
    Paperclip,
    Plus,
    Pencil,
    Trash2,
    Info,
    Copy
  },
  props: {
    templates: {
      type: Array,
      required: true
    },
    templateFilter: {
      type: String,
      required: true
    },
    sourceObjectType: {
      type: String,
      required: false
    },
    showLoadMore: {
      type: Boolean,
      default: false
    }
  },
  emits: ["edit"],
  data() {
    return {
      ALL_EMITTERS,
      cloned_templates: [],
      show_all_templates: false
    }
  },
  computed: {
    color() {
      return this.sourceObjectType == "plan"
        ? "#0D8ABA"
        : this.sourceObjectType == "goal"
          ? "#33CE5F"
          : this.sourceObjectType == "task"
            ? "#B833CE"
            : this.sourceObjectType == "interaction"
              ? "#ffb600fa"
              : "#000000"
    },
    filtered_templates() {
      return this.cloned_templates.filter((t) => !this.templateFilter || t.name.toLowerCase().includes(this.templateFilter.toLowerCase()))
    }
  },
  watch: {
    templates: {
      deep: true,
      handler() {
        this.cloned_templates = this.$utils.clone(this.templates)
      }
    }
  },
  activated() {
    this.register_listeners()
  },
  deactivated() {
    this.unregister_listeners()
  },
  created() {
    this.cloned_templates = this.$utils.clone(this.templates)
    this.register_listeners()
  },
  methods: {
    register_listeners() {
      /*this.$emitter.on(ALL_EMITTERS.TEMPLATE_DELETED, (template_id) => {
        let index = this.cloned_templates.findIndex((item) => item.id == template_id)
        if (index != -1) {
          this.cloned_templates.splice(index, 1)
        }
      })*/
    },
    unregister_listeners() {
      //this.$emitter.off(ALL_EMITTERS.TEMPLATE_DELETED)
    },
    useTemplate(template) {
      if (template.source_object_type == "plan") {
        this.$emitter.emit(ALL_EMITTERS.CREATE_PLAN, { selected_template: template, is_edit_template: false })
      } else if (template.source_object_type == "goal") {
        this.$emitter.emit(ALL_EMITTERS.CREATE_GOAL, { selected_template: template, is_edit_template: false })
      } else if (template.source_object_type == "task") {
        this.$emitter.emit(ALL_EMITTERS.CREATE_TASK, { selected_template: template, is_edit_template: false })
      } else if (template.source_object_type == "interaction") {
        this.$emitter.emit(ALL_EMITTERS.CREATE_INTERACTION, { selected_template: template, is_edit_template: false })
      }
    },
    editTemplate(template) {
      if (template.source_object_type == "plan") {
        this.$emitter.emit(ALL_EMITTERS.CREATE_PLAN, { selected_template: template, is_edit_template: true })
      } else if (template.source_object_type == "goal") {
        this.$emitter.emit(ALL_EMITTERS.CREATE_GOAL, { selected_template: template, is_edit_template: true })
      } else if (template.source_object_type == "task") {
        this.$emitter.emit(ALL_EMITTERS.CREATE_TASK, { selected_template: template, is_edit_template: true })
      } else if (template.source_object_type == "interaction") {
        this.$emitter.emit(ALL_EMITTERS.CREATE_INTERACTION, { selected_template: template, is_edit_template: true })
      } else if (template.source_object_type == "milestone") {
        this.$emit("edit", template)
      }
    },
    duplicateTemplate(template) {
      dataService.duplicateTemplate(template.id).then((res) => {
        this.$emitter.emit(ALL_EMITTERS.NEW_ACTIVITY_TEMPLATE_ADDED, res.data)
        this.$toast.open({
          message: `Template duplicated successfully.`,
          position: "top",
          type: "success",
          duration: 3000
        })
      })
    }
  }
}
</script>
